/**
 * BitchatPeripheral — Capacitor plugin interface for BLE peripheral mode.
 *
 * Web Bluetooth and the @capacitor-community/bluetooth-le plugin are both
 * central-only: they can scan and connect, but cannot advertise a GATT
 * service so that OTHER devices discover and connect to us. Becoming a full
 * Bitchat mesh peer requires the peripheral role, which needs native code:
 *   - iOS:     CBPeripheralManager (CoreBluetooth)
 *   - Android: BluetoothGattServer + BluetoothLeAdvertiser
 *
 * This is the JS proxy. The native implementations live in:
 *   - ios/App/App/BitchatPeripheralPlugin.swift
 *   - android/app/src/main/java/com/bisawofa/app/BitchatPeripheralPlugin.kt
 *
 * All raw byte payloads cross the Capacitor bridge as base64 strings, since
 * the bridge does not marshal Uint8Array/ArrayBuffer reliably.
 *
 * --- API ---------------------------------------------------------------
 *
 * startAdvertising({ serviceUuid, characteristicUuid }): Promise<void>
 *   Begin advertising the Bitchat service UUID and stand up a GATT server
 *   with one characteristic (notify + write + writeWithoutResponse + read).
 *
 * stopAdvertising(): Promise<void>
 *   Stop advertising and tear down the GATT server.
 *
 * notify({ data }): Promise<void>
 *   Push `data` (base64) to every subscribed central via a characteristic
 *   value-change notification. This is how our outbound packets leave.
 *
 * isSupported(): Promise<{ supported: boolean }>
 *   Whether BLE peripheral mode is available (false on web, false on
 *   hardware/OS that lacks LE peripheral support).
 *
 * --- Events (addListener) ---------------------------------------------
 *
 * 'onWrite'        { value: base64, centralId: string }
 *   A connected central wrote bytes to our characteristic.
 *
 * 'onSubscribe'    { centralId: string }
 *   A central enabled notifications (CCCD) on our characteristic.
 *   The transport responds by asking MeshClient to (re)send an announce
 *   so the central binds our sender ID.
 *
 * 'onUnsubscribe'  { centralId: string }
 *   A central disabled notifications or disconnected.
 *
 * 'onStateChange'  { state: 'poweredOn' | 'poweredOff' | 'unauthorized' | 'unsupported' | 'resetting' | 'unknown' }
 *   BLE adapter state transitions (mirrors CBManagerState / Android adapter state).
 */

import { registerPlugin } from '@capacitor/core';

export const BitchatPeripheral = registerPlugin('BitchatPeripheral', {
  web: () => import('./bitchatPeripheral.web.js').then((m) => new m.BitchatPeripheralWeb()),
});

export default BitchatPeripheral;
